import { useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { RiFileTextLine } from "@remixicon/react";
import { useAuthStore } from "@/store/authStore";
import { NavigationSidebar } from "@/features/navigation";
import { Shortlist } from "@/features/bids";
import FlightTracker from "../components/FlightTracker";
import FlightInfoCard from "../components/FlightInfoCard";
import { AircraftSearchBar } from "@/features/search";
import { useFlightStatusAutomation } from "../hooks/useFlightStatusAutomation";
import { useGroundParkedStatus } from "../hooks/useGroundParkedStatus";
import { useQuotesCatchup } from "@/features/quotes/hooks/useQuotesCatchup";
import { Card, CardContent } from "@components/ui/Card";
import { Skeleton } from "@components/ui/Skeleton";
import { getEffectiveAircraft } from "@/features/tails/utils";

const FlightInfoCardSkeleton = () => (
  <Card className="overflow-hidden !rounded-3xl w-full md:w-[420px]">
    <CardContent className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32 !rounded-full" />
        <Skeleton className="h-6 w-20 !rounded-full" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-20 !rounded-full" />
          <Skeleton className="h-5 w-5 !rounded-full" />
          <Skeleton className="h-7 w-20 !rounded-full" />
        </div>
        <Skeleton className="h-5 w-16 !rounded-full" />
      </div>
      <div className="flex items-center gap-4">
        <Skeleton className="h-4 w-24 !rounded-full" />
        <Skeleton className="h-4 w-20 !rounded-full" />
      </div>
    </CardContent>
  </Card>
);

const TrackFlightView = () => {
  const { flightId, tailNumber } = useParams();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();
  const [liveData, setLiveData] = useState(null);

  const { data: flightData, isLoading, isFetching } = useQuery({
    queryKey: ['flightDetails', flightId],
    queryFn: () => apiClient.getFlights({
      flight_id: flightId,
      include: 'quotes,quotes.client,aircrafts,aircrafts.operator',
    }),
    enabled: !!flightId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // call flight by by flightid

  const rawFlight = Array.isArray(flightData) ? flightData[0] : flightData;
  // Layer the per-quote aircraft override onto the flight's aircraft
  // record BEFORE any consumer reads it — FlightInfoCard, FlightDetailDrawer,
  // and the live-tracker fetch all reach into flight.aircraft.tail_number
  // and getAircraftModelName(flight.aircraft), so shipping a pre-merged
  // aircraft is the cheapest way to keep every downstream read consistent.
  // Idempotent for aircraft with no override — getEffectiveAircraft returns
  // the base record unchanged when quote.files.aircraft_overrides is empty.
  const flight = useMemo(() => {
    if (!rawFlight) return rawFlight;
    return {
      ...rawFlight,
      aircraft: getEffectiveAircraft(rawFlight.aircraft, rawFlight.quote),
    };
  }, [rawFlight]);
  const activeTailNumber = tailNumber || flight?.aircraft?.tail_number || null;

  const handleFlightData = useCallback((data) => {
    setLiveData(data);
  }, []);

  // Ground-parked fallback (Flight Summary Patterns · Pattern 01).
  // When Live Positions returns nothing (transponder off on the ground,
  // or aircraft parked and idle), fall back to flight-summary on a ±72h/+24h
  // window and identify the tail's most recent landed leg. Skipped as soon
  // as Live Positions returns anything — we only need this when live is
  // silent. Gated on non-terminal flight_status: once a leg is completed,
  // "parked at destination" is stating the obvious and just clutter.
  const dbStatus = (flight?.flight_status || '').toUpperCase();
  const isTerminal = ['COMPLETED', 'CANCELLED', 'DIVERTED'].includes(dbStatus);
  const groundStatus = useGroundParkedStatus(activeTailNumber, {
    enabled: !!activeTailNumber && !liveData && !isTerminal,
  });

  // Automated status transitions: DELAYED, IN_TRANSIT, COMPLETED, DIVERTED
  useFlightStatusAutomation(flight, liveData);

  // Catchup-on-mount: if this flight is past its date and still non-terminal,
  // call FR24 summary once and reconcile the status (same logic as the /track
  // list catchup, scoped to the open flight). Reuses the per-flight-ID dedup
  // in sessionStorage so we don't re-check a flight already swept elsewhere.
  const flightForCatchup = useMemo(() => (flight ? [flight] : []), [flight]);
  useQuotesCatchup(flightForCatchup);

  return createPortal(
    <>
      {/* Sidebar + Shortlist for authenticated users */}
      {isAuthenticated && (
        <div className="group/nav relative z-[35]">
          <NavigationSidebar onNavigate={(route) => navigate(route)} />
          <div className="fixed bottom-6 left-6 z-40 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-2 w-16 group-hover/nav:w-56 hover:w-56 transition-all duration-300 ease-in-out group-hover/nav:px-4 hover:px-4 hidden md:flex flex-col gap-2 group/shortlist">
            <Shortlist
              onViewBid={(bid) => console.log('View bid:', bid)}
              useRelativePosition={true}
            />
            <button
              onClick={() => navigate('/quotes/generate?quickquote=true')}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white p-3 rounded-xl hover:bg-primary-700 transition-colors w-full"
            >
              <RiFileTextLine className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm hidden group-hover/nav:inline group-hover/shortlist:inline whitespace-nowrap">Quick Quote</span>
            </button>
          </div>
        </div>
      )}

      {/* Map fills entire viewport - below sidebar z-[30] */}
      <div className="fixed inset-0 z-[25]">
        <FlightTracker dbFlight={flight} tailNumber={activeTailNumber} groundStatus={groundStatus} onFlightData={handleFlightData} />
      </div>

      {/* Header bar with title and search */}
      <div className={`fixed top-0 left-0 right-0 z-[29] ${isAuthenticated ? 'ml-0 md:ml-24' : 'ml-0'} px-4 md:px-6 py-3 md:py-4`}>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl md:text-5xl font-semibold text-primary-600 shrink-0">
            Track Flight
          </h1>
          {isAuthenticated && (
            <div className="hidden md:block w-96">
              <AircraftSearchBar placeholder="Search Aircraft" />
            </div>
          )}
        </div>
      </div>

      {/* Info card overlay - bottom-left, right of sidebar */}
      <div className={`fixed bottom-4 md:bottom-6 left-0 right-0 md:right-auto ${isAuthenticated ? 'md:left-28' : 'md:left-6'} z-[29] px-4 md:px-0`}>
        {flight ? (
          <FlightInfoCard flight={flight} liveData={liveData} tailSet={tailNumber} groundStatus={groundStatus} />
        ) : (isLoading || isFetching) ? (
          <FlightInfoCardSkeleton />
        ) : null}
      </div>
    </>,
    document.body
  );
};

export default TrackFlightView;
